import { useState, useRef } from 'react'
import CloseIcon from '../../../assets/Close.svg'
import { FormTexts } from '../../../language/Texts'
import { ComponentType } from '../../../models/componentType.enum'
import './InputComponent.scss'

interface InputCompProps {
    label: string
    value: string
    handleChange: (value: string) => void
    isRequired?: boolean
    type?: string,
    componentType?: ComponentType
    showUrlError?: boolean
    setShowUrlError?: (show: boolean) => void
}

// Helper function to check if URL has protocol
export const hasProtocol = (url: string): boolean => {
    return /^https?:\/\//.test(url)
}

// Helper function to add https:// if no protocol exists
export const addHttpsProtocol = (url: string): string => {
    if (!url) return url
    if (hasProtocol(url)) return url
    return `https://${url}`
}

// Helper function to validate URL - exported for use in form validation
export const isValidUrl = (url: string): boolean => {
    if (!url) return true
    try {
        const urlWithProtocol = addHttpsProtocol(url)
        const urlObj = new URL(urlWithProtocol)
        // Check if the hostname has at least one dot (e.g., example.com)
        // AND contains at least one letter (not just numbers)
        return urlObj.hostname.includes('.') && /[a-zA-Z]/.test(urlObj.hostname)
    } catch {
        return false
    }
}

function InputComponent({ label, value, handleChange, isRequired = false, type = 'text', componentType = ComponentType.DIGITAL_NAMEPLATE, showUrlError = false, setShowUrlError }: InputCompProps) {
    const [isFocused, setIsFocused] = useState(false)
    const [internalUrlError, setInternalUrlError] = useState(false)
    const [userTypedProtocol, setUserTypedProtocol] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    // Use external error state if provided, otherwise use internal
    const urlError = showUrlError || internalUrlError

    // For URL type: show without protocol only if user didn't type it
    // If user typed protocol, show exactly what they typed
    const getDisplayValue = () => {
        if (type !== 'url') return value
        if (!value) return ''
        if (userTypedProtocol) return value
        // User didn't type protocol, so strip it for display
        return value.replace(/^https?:\/\//, '')
    }
    const displayValue = getDisplayValue()

    const onChange = (inputValue: string) => {
        if (label === FormTexts.serialNumberForm) {
            inputValue = inputValue.replace(/[./]/g, '')
        }
        
        if (type === 'url') {
            // Clear error when user starts typing
            setInternalUrlError(false)
            if (setShowUrlError) setShowUrlError(false)
            
            // Track if user typed the protocol themselves
            const userHasProtocol = hasProtocol(inputValue)
            setUserTypedProtocol(userHasProtocol)
            
            if (inputValue) {
                // Always store with https:// protocol in background
                handleChange(addHttpsProtocol(inputValue))
            } else {
                handleChange('')
            }
        } else {
            handleChange(inputValue)
        }
    }

    const handleClear = () => {
        handleChange('')
        setInternalUrlError(false)
        setUserTypedProtocol(false)
        if (setShowUrlError) setShowUrlError(false)
    }

    const handleFocus = () => {
        setIsFocused(true)
    }

    const handleBlur = () => {
        setIsFocused(false)
        
        // Validate URL on blur
        if (type === 'url' && value && !isValidUrl(value)) {
            setInternalUrlError(true)
            if (setShowUrlError) setShowUrlError(true)
            // Focus back on the input
            setTimeout(() => {
                inputRef.current?.focus()
            }, 0)
            // Clear error after 3 seconds
            setTimeout(() => {
                setInternalUrlError(false)
                if (setShowUrlError) setShowUrlError(false)
            }, 3000)
        }
    }

    return (
        <div
            className={`input_pair ${componentType === ComponentType.GEMUE_EXTERNAL ? 'gemue_input_pair' : ''}`}

        >
            <label
                className={`input_label ${componentType === ComponentType.GEMUE_EXTERNAL ? 'gemue_input_label' : ''}`}
            >{label}</label>
            <div className="input_container">
                <input
                    ref={inputRef}
                    style={{ width: type === 'email' && window.innerWidth > 768 ? "var(--inputPairWidth)" : undefined }}
                    data-testid={label}
                    className={`input_field ${componentType === ComponentType.GEMUE_EXTERNAL ? 'gemue_input_field' : ''} ${urlError ? 'input_error' : ''}`}
                    required={isRequired}
                    type={type === 'url' ? 'text' : type}
                    value={displayValue}
                    onChange={e => onChange(e.target.value)}
                    onFocus={handleFocus}
                    onBlur={handleBlur}
                />
                {displayValue && isFocused && (
                    <button
                        type="button"
                        className={`${componentType === ComponentType.GEMUE_EXTERNAL ? 'gemue_input_clear_button' : 'input_clear_button '}`}
                        onClick={handleClear}
                        onMouseDown={(e) => e.preventDefault()}
                        aria-label="Clear input"
                        tabIndex={-1}
                    >
                        <img src={CloseIcon} alt="Clear" className="clear_icon default_icon" />
                    </button>
                )}
                {urlError && (
                    <div className="url_error_message">Please enter a valid URL</div>
                )}
            </div>
        </div>
    )
}

export default InputComponent